import { useCallback, useEffect, useReducer, useRef, type ReactNode, type ChangeEvent } from 'react'
import type { Editor } from '@tiptap/react'
import {
  Bold,
  Italic,
  Strikethrough,
  Undo2,
  Redo2,
  Link2,
  Unlink,
  Image as ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Eraser,
  Underline,
  Video as YoutubeIcon,
} from 'lucide-react'
import '../Assets/css/EditorToolbar.css'

export type ImageUploadHandler = (editor: Editor, file: File) => void | Promise<void>

interface EditorToolbarProps {
  editor: Editor | null
  onImageUpload?: ImageUploadHandler
}

/**
 * TipTap 에디터용 툴바
 *
 * 사용법:
 *   <EditorToolbar editor={editor} onImageUpload={handleImageUpload} />
 */
export default function EditorToolbar({ editor, onImageUpload }: EditorToolbarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0)
  const suppressToolbarSyncRef = useRef(false)

  useEffect(() => {
    if (!editor) return

    const rerender = () => {
      if (suppressToolbarSyncRef.current) return
      forceUpdate()
    }
    editor.on('selectionUpdate', rerender)
    editor.on('transaction', rerender)
    editor.on('update', rerender)

    return () => {
      editor.off('selectionUpdate', rerender)
      editor.off('transaction', rerender)
      editor.off('update', rerender)
    }
  }, [editor])
  const setYoutubeVideo = useCallback(() => {
    if (!editor) return
    const url = window.prompt('유튜브 영상 URL을 입력하세요')
    if (!url) return

    const inserted = editor.commands.setYoutubeVideo({ src: url })
    if (!inserted) {
      alert('올바른 유튜브 URL이 아니에요')
    }
  }, [editor])
  const onColorPickerOpen = useCallback(() => {
    suppressToolbarSyncRef.current = true
  }, [])

  const onColorPickerClose = useCallback(() => {
    suppressToolbarSyncRef.current = false
    forceUpdate()
  }, [])

  const setLink = useCallback(() => {
    if (!editor) return
    const previousUrl = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('연결할 URL을 입력하세요', previousUrl || 'https://')

    if (url === null) return // 취소

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }, [editor])

  const onPickImage = () => fileInputRef.current?.click()

  const onImageFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && onImageUpload && editor) {
      onImageUpload(editor, file)
    }
    e.target.value = ''
  }
const FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '40px']

function FontSizeControl({ editor }: { editor: Editor }) {
  const currentSize = editor.getAttributes('textStyle').fontSize || ''

  return (
    <select
      className="editor-toolbar__select"
      value={currentSize}
      onChange={(e) => {
        const value = e.target.value
        if (value) editor.chain().focus().setFontSize(value).run()
        else editor.chain().focus().unsetFontSize().run()
      }}
      aria-label="글자 크기"
    >
      <option value="">기본</option>
      {FONT_SIZES.map((size) => (
        <option key={size} value={size}>
          {size}
        </option>
      ))}
    </select>
  )
}

function ColorControl({
  editor,
  onPickerOpen,
  onPickerClose,
}: {
  editor: Editor
  onPickerOpen: () => void
  onPickerClose: () => void
}) {
  const currentColor = editor.getAttributes('textStyle').color || '#000000'

  return (
    <div className="editor-toolbar__color">
      <input
        type="color"
        value={currentColor}
        onChange={(e) => editor.chain().setColor(e.target.value).run()}
        onMouseDown={onPickerOpen}
        onTouchStart={onPickerOpen}
        onBlur={onPickerClose}
        title="글자 색상"
        aria-label="글자 색상"
      />
      <ToolbarButton onClick={() => editor.chain().focus().unsetColor().run()} label="색상 초기화">
        <Eraser size={16} />
      </ToolbarButton>
    </div>
  )
}
  if (!editor) return null

  return (
    <div className="editor-toolbar" role="toolbar" aria-label="에디터 서식 도구">
      <ToolbarGroup>
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          label="실행 취소"
        >
          <Undo2 size={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          label="다시 실행"
        >
          <Redo2 size={18} />
        </ToolbarButton>
      </ToolbarGroup>

      <Divider />
      
      <ToolbarGroup>
        <FontSizeControl editor={editor} />
        <ColorControl
          editor={editor}
          onPickerOpen={onColorPickerOpen}
          onPickerClose={onColorPickerClose}
        />
      </ToolbarGroup>


      <Divider />

      <ToolbarGroup>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          label="굵게"
        >
          <Bold size={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          label="기울임"
        >
          <Italic size={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive('strike')}
          label="취소선"
        >
          <Strikethrough size={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          label="밑줄"
        >
          <Underline size={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          label="서식 지우기"
        >
          <Eraser size={18} />
        </ToolbarButton>
      </ToolbarGroup>

      <Divider />

      <ToolbarGroup>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
          label="왼쪽 정렬"
        >
          <AlignLeft size={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
          label="가운데 정렬"
        >
          <AlignCenter size={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          active={editor.isActive({ textAlign: 'right' })}
          label="오른쪽 정렬"
        >
          <AlignRight size={18} />
        </ToolbarButton>
      </ToolbarGroup>

      <Divider />

      {/* <ToolbarGroup>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          label="글머리 기호 목록"
        >
          <List size={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          label="번호 매기기 목록"
        >
          <ListOrdered size={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
          label="인용구"
        >
          <Quote size={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          label="구분선"
        >
          <Minus size={18} />
        </ToolbarButton>
      </ToolbarGroup> */}

      <Divider />

      <ToolbarGroup>
        <ToolbarButton onClick={setLink} active={editor.isActive('link')} label="링크 삽입">
          <Link2 size={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().unsetLink().run()}
          disabled={!editor.isActive('link')}
          label="링크 제거"
        >
          <Unlink size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={onPickImage} label="이미지 삽입">
          <ImageIcon size={18} />
        </ToolbarButton>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onImageFileChange}
            style={{ display: 'none' }}
          />
      </ToolbarGroup>
      <ToolbarButton onClick={setYoutubeVideo} label="유튜브 삽입">
        <YoutubeIcon size={18} />
      </ToolbarButton>
    </div>
  )
}

function ToolbarGroup({ children }: { children: ReactNode }) {
  return <div className="editor-toolbar__group">{children}</div>
}

function Divider() {
  return <span className="editor-toolbar__divider" />
}

interface ToolbarButtonProps {
  onClick: () => void
  active?: boolean
  disabled?: boolean
  label: string
  children: ReactNode
}

function ToolbarButton({ onClick, active, disabled, label, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      className={`editor-toolbar__btn${active ? ' is-active' : ''}`}
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      aria-pressed={!!active}
    >
      {children}
    </button>
  )
}